/* ==========================================================================
   THEME CORE
   Shared utilities plus the custom elements every template relies on:
   overlays, the sticky header, scroll reveal, accordions, quantity steppers,
   toasts and the wishlist store.

   Everything here is vanilla — no framework, no bundler. Elements upgrade
   themselves when the browser parses them, so sections can be added and
   reordered in the Theme Editor without any wiring.
   ========================================================================== */

(function () {
  'use strict';

  document.documentElement.classList.add('js');

  /* ------------------------------------------------------------------------
     Small helpers, exposed on window.Theme for the other bundles.
     ------------------------------------------------------------------------ */

  const Theme = (window.Theme = window.Theme || {});

  Theme.settings = window.themeSettings || {};
  Theme.strings = window.themeStrings || {};

  Theme.on = function (name, handler) {
    document.addEventListener(name, handler);
    return () => document.removeEventListener(name, handler);
  };

  Theme.emit = function (name, detail) {
    document.dispatchEvent(new CustomEvent(name, { detail, bubbles: true }));
  };

  Theme.debounce = function (fn, wait) {
    let timer;
    return function (...args) {
      clearTimeout(timer);
      timer = setTimeout(() => fn.apply(this, args), wait);
    };
  };

  Theme.formatMoney = function (cents) {
    const format = Theme.settings.moneyFormat || '${{amount}}';
    const amount = Number(cents || 0) / 100;

    // Shopify money formats carry their own decimal/comma convention. Match
    // the placeholder rather than guessing, so LBP, EUR and USD all render the
    // way the merchant configured them in Admin.
    return format.replace(/\{\{\s*(\w+)\s*\}\}/g, function (_match, token) {
      switch (token) {
        case 'amount':
          return withDelimiters(amount, 2, ',', '.');
        case 'amount_no_decimals':
          return withDelimiters(amount, 0, ',', '.');
        case 'amount_with_comma_separator':
          return withDelimiters(amount, 2, '.', ',');
        case 'amount_no_decimals_with_comma_separator':
          return withDelimiters(amount, 0, '.', ',');
        case 'amount_with_space_separator':
          return withDelimiters(amount, 2, ' ', ',');
        case 'amount_no_decimals_with_space_separator':
          return withDelimiters(amount, 0, ' ', ' ');
        case 'amount_with_apostrophe_separator':
          return withDelimiters(amount, 2, "'", '.');
        default:
          return withDelimiters(amount, 2, ',', '.');
      }
    });
  };

  function withDelimiters(number, precision, thousands, decimal) {
    const fixed = number.toFixed(precision);
    const parts = fixed.split('.');
    const whole = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1' + thousands);
    return parts[1] ? whole + decimal + parts[1] : whole;
  }

  /** Read a named <section> out of a Section Rendering API response. */
  Theme.parseSection = function (html, selector) {
    return new DOMParser()
      .parseFromString(html, 'text/html')
      .querySelector(selector);
  };

  /** Focusable descendants, in document order. */
  const FOCUSABLE =
    'a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled]), summary, [tabindex]:not([tabindex="-1"])';

  Theme.focusable = function (root) {
    return Array.from(root.querySelectorAll(FOCUSABLE)).filter(
      (el) => el.offsetParent !== null || el === document.activeElement
    );
  };

  /* ------------------------------------------------------------------------
     Scroll lock — refcounted, so nested overlays cannot unlock each other.
     ------------------------------------------------------------------------ */

  let lockCount = 0;
  let savedScroll = 0;

  /*
    `overflow: hidden` on the body is ignored by iOS Safari, so the page is
    taken out of flow and offset by the scroll position it had. That pins it
    completely: scrolling inside a drawer can never move the page behind it.
    The offset is handed to CSS and the position restored on close.
  */
  Theme.lockScroll = function () {
    if (lockCount === 0) {
      savedScroll = window.scrollY;

      // Compensate for the scrollbar that is about to disappear, so the page
      // behind does not jump sideways as the drawer opens.
      const gap = window.innerWidth - document.documentElement.clientWidth;
      if (gap > 0) document.body.style.paddingRight = gap + 'px';

      document.documentElement.style.setProperty('--scroll-lock-offset', -savedScroll + 'px');
      document.body.classList.add('is-overlay-open');
    }
    lockCount += 1;
  };

  Theme.unlockScroll = function () {
    lockCount = Math.max(0, lockCount - 1);
    if (lockCount > 0) return;

    document.body.classList.remove('is-overlay-open');
    document.body.style.paddingRight = '';
    document.documentElement.style.removeProperty('--scroll-lock-offset');

    // Taking the body out of `position: fixed` sends the page to the top, so
    // put it back where the shopper left it — without a smooth animation.
    window.scrollTo({ top: savedScroll, behavior: 'instant' });
  };

  /* ========================================================================
     <theme-overlay> — the shared drawer / modal shell.

     Handles opening, closing, the scrim, Escape, focus trapping and returning
     focus to whatever opened it. Every drawer in the theme is one of these.
     ======================================================================== */

  class ThemeOverlay extends HTMLElement {
    constructor() {
      super();
      this.onKeydown = this.onKeydown.bind(this);
      this.onScrimClick = this.onScrimClick.bind(this);
    }

    connectedCallback() {
      this.panel = this.querySelector('[data-overlay-panel]');
      this.scrim = this.querySelector('[data-overlay-scrim]');

      if (this.scrim) this.scrim.addEventListener('click', this.onScrimClick);

      this.querySelectorAll('[data-overlay-close]').forEach((btn) =>
        btn.addEventListener('click', () => this.close())
      );

      // Openers can live anywhere on the page; they address this overlay by id.
      document
        .querySelectorAll('[data-overlay-open="' + this.id + '"]')
        .forEach((btn) =>
          btn.addEventListener('click', (event) => {
            event.preventDefault();
            this.open(btn);
          })
        );
    }

    disconnectedCallback() {
      if (this.isOpen) this.close({ silent: true });
    }

    get isOpen() {
      return this.classList.contains('is-open');
    }

    open(opener) {
      if (this.isOpen) return;
      this.opener = opener || document.activeElement;
      this.classList.add('is-open');
      this.removeAttribute('aria-hidden');
      Theme.lockScroll();
      document.addEventListener('keydown', this.onKeydown);

      // Wait a frame so the panel is painted before focus moves into it,
      // otherwise Safari scrolls the page to the pre-transform position.
      requestAnimationFrame(() => {
        const target =
          this.querySelector('[data-overlay-autofocus]') ||
          Theme.focusable(this.panel || this)[0] ||
          this.panel;
        if (target) target.focus({ preventScroll: true });
      });

      Theme.emit('overlay:open', { overlay: this });
    }

    close(options) {
      if (!this.isOpen) return;
      this.classList.remove('is-open');
      this.setAttribute('aria-hidden', 'true');
      Theme.unlockScroll();
      document.removeEventListener('keydown', this.onKeydown);

      if (!options || !options.silent) {
        if (this.opener && document.contains(this.opener)) {
          this.opener.focus({ preventScroll: true });
        }
      }
      this.opener = null;
      Theme.emit('overlay:close', { overlay: this });
    }

    toggle(opener) {
      this.isOpen ? this.close() : this.open(opener);
    }

    onScrimClick() {
      this.close();
    }

    onKeydown(event) {
      if (event.key === 'Escape') {
        event.stopPropagation();
        this.close();
        return;
      }
      if (event.key !== 'Tab') return;

      const items = Theme.focusable(this.panel || this);
      if (items.length === 0) return;

      const first = items[0];
      const last = items[items.length - 1];

      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    }
  }

  customElements.define('theme-overlay', ThemeOverlay);

  /** Open an overlay by id from anywhere (used by cart.js after an add). */
  Theme.openOverlay = function (id, opener) {
    const el = document.getElementById(id);
    if (el && typeof el.open === 'function') el.open(opener);
    return el;
  };

  Theme.closeOverlay = function (id) {
    const el = document.getElementById(id);
    if (el && typeof el.close === 'function') el.close();
  };

  Theme.closeAllOverlays = function () {
    document.querySelectorAll('theme-overlay.is-open').forEach((el) => el.close());
  };

  /* ========================================================================
     <sticky-header> — hairline + compaction on scroll.
     ======================================================================== */

  class StickyHeader extends HTMLElement {
    connectedCallback() {
      this.threshold = 4;
      this.ticking = false;
      this.onScroll = this.onScroll.bind(this);

      window.addEventListener('scroll', this.onScroll, { passive: true });
      window.addEventListener('resize', () => this.publishHeight(), { passive: true });

      // Defer the first read a frame: a page restored mid-scroll still gets its
      // hairline, without a layout read during parse.
      requestAnimationFrame(() => {
        this.onScroll();
        this.publishHeight();
      });
    }

    disconnectedCallback() {
      window.removeEventListener('scroll', this.onScroll);
    }

    onScroll() {
      if (this.ticking) return;
      this.ticking = true;
      requestAnimationFrame(() => {
        this.classList.toggle('is-scrolled', window.scrollY > this.threshold);
        this.ticking = false;
      });
    }

    /* The sticky offset feeds scroll-padding and every sticky sidebar. */
    publishHeight() {
      const height = this.offsetHeight;
      document.documentElement.style.setProperty('--sticky-offset', height + 'px');
    }
  }

  customElements.define('sticky-header', StickyHeader);

  /* ========================================================================
     <mega-menu> — hover/focus dropdown with a close delay so the pointer can
     travel from the trigger to the panel without the menu snapping shut.
     ======================================================================== */

  class MegaMenu extends HTMLElement {
    connectedCallback() {
      this.items = Array.from(this.querySelectorAll('[data-mega-item]'));
      this.timer = null;

      this.items.forEach((item) => {
        const link = item.querySelector('[data-mega-trigger]');
        if (!link) return;

        item.addEventListener('mouseenter', () => this.openItem(item));
        item.addEventListener('mouseleave', () => this.scheduleClose());
        link.addEventListener('focus', () => this.openItem(item));

        // A trigger with a panel opens it on ArrowDown rather than navigating
        // away, so the menu is reachable without a mouse.
        link.addEventListener('keydown', (event) => {
          if (event.key === 'ArrowDown' && item.querySelector('.mega')) {
            event.preventDefault();
            this.openItem(item);
            const first = item.querySelector('.mega a');
            if (first) first.focus();
          }
        });
      });

      this.addEventListener('focusout', (event) => {
        if (!this.contains(event.relatedTarget)) this.closeAll();
      });

      this.addEventListener('mouseleave', () => this.scheduleClose());

      document.addEventListener('keydown', (event) => {
        if (event.key === 'Escape') this.closeAll();
      });
    }

    openItem(item) {
      clearTimeout(this.timer);
      if (!item.querySelector('.mega')) {
        this.closeAll();
        return;
      }
      this.items.forEach((other) => {
        const open = other === item;
        other.classList.toggle('is-open', open);
        const trigger = other.querySelector('[data-mega-trigger]');
        if (trigger && other.querySelector('.mega')) {
          trigger.setAttribute('aria-expanded', String(open));
        }
      });
    }

    scheduleClose() {
      clearTimeout(this.timer);
      this.timer = setTimeout(() => this.closeAll(), 140);
    }

    closeAll() {
      clearTimeout(this.timer);
      this.items.forEach((item) => {
        item.classList.remove('is-open');
        const trigger = item.querySelector('[data-mega-trigger]');
        if (trigger && item.querySelector('.mega')) {
          trigger.setAttribute('aria-expanded', 'false');
        }
      });
    }
  }

  customElements.define('mega-menu', MegaMenu);

  /* ========================================================================
     <mobile-nav> — accordion inside the left drawer.
     ======================================================================== */

  class MobileNav extends HTMLElement {
    connectedCallback() {
      this.querySelectorAll('[data-mobile-toggle]').forEach((button) => {
        button.addEventListener('click', () => {
          const panel = document.getElementById(button.getAttribute('aria-controls'));
          if (!panel) return;
          const open = button.getAttribute('aria-expanded') === 'true';
          button.setAttribute('aria-expanded', String(!open));
          panel.classList.toggle('is-open', !open);
        });
      });
    }
  }

  customElements.define('mobile-nav', MobileNav);

  /* ========================================================================
     <theme-accordion> — animates <details> open/close by height.
     ======================================================================== */

  class ThemeAccordion extends HTMLElement {
    connectedCallback() {
      this.querySelectorAll('details').forEach((details) => {
        const summary = details.querySelector('summary');
        const panel = details.querySelector('[data-accordion-panel]');
        if (!summary || !panel) return;

        summary.addEventListener('click', (event) => {
          if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
          event.preventDefault();

          if (details.open) {
            panel.style.height = panel.scrollHeight + 'px';
            requestAnimationFrame(() => {
              panel.style.transition = 'height 0.32s var(--ease-editorial)';
              panel.style.height = '0px';
            });
            panel.addEventListener(
              'transitionend',
              () => {
                details.open = false;
                panel.style.transition = '';
                panel.style.height = '';
              },
              { once: true }
            );
          } else {
            details.open = true;
            panel.style.height = '0px';
            requestAnimationFrame(() => {
              panel.style.transition = 'height 0.32s var(--ease-editorial)';
              panel.style.height = panel.scrollHeight + 'px';
            });
            panel.addEventListener(
              'transitionend',
              () => {
                panel.style.transition = '';
                panel.style.height = '';
              },
              { once: true }
            );
          }
        });
      });
    }
  }

  customElements.define('theme-accordion', ThemeAccordion);

  /* ========================================================================
     <quantity-input> — plus/minus around a number field.
     ======================================================================== */

  class QuantityInput extends HTMLElement {
    connectedCallback() {
      this.input = this.querySelector('input');
      if (!this.input) return;

      this.querySelectorAll('button[data-qty-step]').forEach((button) => {
        button.addEventListener('click', () => {
          const step = Number(button.dataset.qtyStep);
          const min = Number(this.input.min || 0);
          const max = this.input.max ? Number(this.input.max) : Infinity;
          const next = Math.min(max, Math.max(min, Number(this.input.value || 0) + step));
          if (next === Number(this.input.value)) return;
          this.input.value = String(next);
          this.input.dispatchEvent(new Event('change', { bubbles: true }));
          this.sync();
        });
      });

      this.input.addEventListener('change', () => this.sync());
      this.sync();
    }

    sync() {
      const value = Number(this.input.value || 0);
      const min = Number(this.input.min || 0);
      const max = this.input.max ? Number(this.input.max) : Infinity;
      const minus = this.querySelector('[data-qty-step="-1"]');
      const plus = this.querySelector('[data-qty-step="1"]');
      if (minus) minus.disabled = value <= min;
      if (plus) plus.disabled = value >= max;
    }
  }

  customElements.define('quantity-input', QuantityInput);

  /* ========================================================================
     Scroll reveal — one observer for the whole document.
     ======================================================================== */

  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

  let revealObserver = null;

  function observeReveals(root) {
    const targets = (root || document).querySelectorAll('[data-reveal]:not(.is-revealed)');
    if (targets.length === 0) return;

    if (reduceMotion.matches || !('IntersectionObserver' in window)) {
      targets.forEach((el) => el.classList.add('is-revealed'));
      return;
    }

    if (!revealObserver) {
      revealObserver = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (!entry.isIntersecting) return;
            entry.target.classList.add('is-revealed');
            revealObserver.unobserve(entry.target);
          });
        },
        { rootMargin: '0px 0px -8% 0px', threshold: 0.06 }
      );
    }

    targets.forEach((el) => revealObserver.observe(el));
  }

  Theme.observeReveals = observeReveals;

  /* ========================================================================
     Toasts — the confirmation after add-to-cart, wishlist, newsletter.
     ======================================================================== */

  const ICONS = {
    success:
      '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><path d="M20 6L9 17l-5-5"/></svg>',
    error:
      '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="9"/><path d="M12 8v5M12 16h.01"/></svg>',
  };

  const CLOSE_ICON =
    '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"><path d="M18 6L6 18M6 6l12 12"/></svg>';

  function toastStack() {
    let stack = document.querySelector('.toast-stack');
    if (!stack) {
      stack = document.createElement('div');
      stack.className = 'toast-stack';
      stack.setAttribute('role', 'status');
      stack.setAttribute('aria-live', 'polite');
      document.body.appendChild(stack);
    }
    return stack;
  }

  Theme.toast = function (options) {
    const opts = typeof options === 'string' ? { title: options } : options || {};
    const stack = toastStack();

    const el = document.createElement('div');
    el.className = 'toast' + (opts.variant === 'error' ? ' toast--error' : '');
    el.innerHTML =
      '<span class="toast__icon">' +
      (ICONS[opts.variant === 'error' ? 'error' : 'success'] || '') +
      '</span><div><p class="toast__title"></p>' +
      (opts.text ? '<p class="toast__text"></p>' : '') +
      '</div><button type="button" class="toast__close" aria-label="' +
      (Theme.strings.close || 'Close') +
      '">' +
      CLOSE_ICON +
      '</button>';

    el.querySelector('.toast__title').textContent = opts.title || '';
    if (opts.text) el.querySelector('.toast__text').textContent = opts.text;

    function dismiss() {
      el.classList.add('is-leaving');
      setTimeout(() => el.remove(), 260);
    }

    el.querySelector('.toast__close').addEventListener('click', dismiss);
    stack.appendChild(el);
    setTimeout(dismiss, opts.duration || 4200);

    // Never let a burst of adds fill the screen.
    while (stack.children.length > 3) stack.firstElementChild.remove();
  };

  /* ========================================================================
     Wishlist — a local store, kept in localStorage.

     Deliberately not a Shopify object: Shopify has no native wishlist. The
     markup and events are here so a wishlist app can take it over later
     without any template changes.
     ======================================================================== */

  const WISHLIST_KEY = 'theme:wishlist';

  const Wishlist = (Theme.wishlist = {
    read() {
      try {
        const raw = localStorage.getItem(WISHLIST_KEY);
        const parsed = raw ? JSON.parse(raw) : [];
        return Array.isArray(parsed) ? parsed : [];
      } catch (error) {
        return [];
      }
    },

    write(handles) {
      try {
        localStorage.setItem(WISHLIST_KEY, JSON.stringify(handles));
      } catch (error) {
        /* Private mode / quota — the toggle still works for this page view. */
      }
      Theme.emit('wishlist:change', { handles });
    },

    has(handle) {
      return Wishlist.read().indexOf(String(handle)) !== -1;
    },

    toggle(handle) {
      const key = String(handle);
      const handles = Wishlist.read();
      const index = handles.indexOf(key);
      if (index === -1) handles.push(key);
      else handles.splice(index, 1);
      Wishlist.write(handles);
      return index === -1;
    },

    count() {
      return Wishlist.read().length;
    },
  });

  class WishlistButton extends HTMLElement {
    connectedCallback() {
      this.button = this.querySelector('button') || this;
      this.handle = this.dataset.productHandle;
      this.sync();

      this.button.addEventListener('click', (event) => {
        event.preventDefault();
        const added = Wishlist.toggle(this.handle);
        this.sync();
        Theme.toast({
          title: added
            ? Theme.strings.wishlistAdded || 'Saved to your wishlist'
            : Theme.strings.wishlistRemoved || 'Removed from your wishlist',
          text: this.dataset.productTitle || '',
        });
      });

      this.unsubscribe = Theme.on('wishlist:change', () => this.sync());
    }

    disconnectedCallback() {
      if (this.unsubscribe) this.unsubscribe();
    }

    sync() {
      const saved = Wishlist.has(this.handle);
      this.button.setAttribute('aria-pressed', String(saved));
      const label = saved
        ? Theme.strings.wishlistRemove || 'Remove from wishlist'
        : Theme.strings.wishlistAdd || 'Add to wishlist';
      this.button.setAttribute('aria-label', label);
      const text = this.querySelector('[data-wishlist-label]');
      if (text) text.textContent = label;
    }
  }

  customElements.define('wishlist-button', WishlistButton);

  /* Header wishlist counter. */
  function syncWishlistCount() {
    const count = Wishlist.count();
    document.querySelectorAll('[data-wishlist-count]').forEach((el) => {
      el.textContent = count > 99 ? '99+' : String(count);
      el.hidden = count === 0;
    });
  }

  Theme.on('wishlist:change', syncWishlistCount);

  /* ========================================================================
     Announcement bar rotation
     ======================================================================== */

  class AnnouncementBar extends HTMLElement {
    connectedCallback() {
      this.slides = Array.from(this.querySelectorAll('[data-slide]'));
      if (this.slides.length < 2) return;

      this.index = 0;
      const interval = Number(this.dataset.interval || 5) * 1000;

      this.querySelectorAll('[data-announcement-nav]').forEach((button) => {
        button.addEventListener('click', () => {
          this.go(this.index + Number(button.dataset.announcementNav));
          this.restart(interval);
        });
      });

      this.restart(interval);

      // Pause while the tab is hidden so returning does not fast-forward.
      document.addEventListener('visibilitychange', () => {
        if (document.hidden) clearInterval(this.timer);
        else this.restart(interval);
      });
    }

    disconnectedCallback() {
      clearInterval(this.timer);
    }

    restart(interval) {
      clearInterval(this.timer);
      if (interval > 0) this.timer = setInterval(() => this.go(this.index + 1), interval);
    }

    go(next) {
      const total = this.slides.length;
      this.index = ((next % total) + total) % total;
      this.slides.forEach((slide, i) => {
        slide.classList.toggle('is-active', i === this.index);
        slide.setAttribute('aria-hidden', String(i !== this.index));
      });
    }
  }

  customElements.define('announcement-bar', AnnouncementBar);

  /* ========================================================================
     Boot + Theme Editor hooks
     ======================================================================== */

  function boot(root) {
    observeReveals(root);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
      boot(document);
      syncWishlistCount();
    });
  } else {
    boot(document);
    syncWishlistCount();
  }

  // Sections re-render as the merchant edits them; re-arm anything observer-based.
  document.addEventListener('shopify:section:load', (event) => {
    boot(event.target);
    syncWishlistCount();
  });

  document.addEventListener('shopify:section:select', (event) => {
    const overlay = event.target.querySelector('theme-overlay[data-editor-preview]');
    if (overlay) overlay.open();
  });

  document.addEventListener('shopify:section:deselect', (event) => {
    const overlay = event.target.querySelector('theme-overlay[data-editor-preview]');
    if (overlay) overlay.close();
  });
})();
